/**
 * The Quest journey (quest-journey-2026-10-27, DESIGN.md + prototype.html).
 *
 * One sky, seven constellations from the bottom up: First Star, The Rising,
 * Venus, Zodiac Wheel, The Moon, The Sun, Saturn. The server builds the sky
 * (`sky` v2 on quests/); the app reads it (`readJourney`), draws it
 * (`drawSky`, the prototype's drawings, ported as they are) and answers the
 * three questions at a glance: what's next (`nextCard`), where am I
 * (`stripItems`), what have I done (the lit constellations).
 *
 * Revisions over the prototype (2026-10-27): the Moon's three stars are three
 * nights of coming back (crescent, half, full); the letter sits in the Moon's
 * along the way and only exists when the server sends it; a reply rings a
 * star again as a bonus. Astrology only; no machinery copy; no money before
 * the Community opens (the server's `carats_visible`).
 *
 * Stars are placed by their index in a constellation, so a renamed key never
 * moves a drawing.
 */

// ── the sections ──────────────────────────────────────────────────────────
// Revision 5 (recipient polish 2026-11-03, DESIGN §20.6): the Studio comes
// before the Moon, and the Voice has a constellation of its own, The Chorus.
// This is only the fallback: the order drawn is always the server's
// (`levelOrder`), so an older server still draws its own seven.
export const SECTION_ORDER = ['fs', 'ri', 've', 'zw', 'su', 'mo', 'vo', 'sa']
export const SEC = {
  fs: { name: 'First Star', h: 250, room: 'your Journal', short: 'Journal' },
  ri: { name: 'The Rising', h: 250, room: 'your Altar', short: 'Altar' },
  ve: { name: 'Venus', h: 250, room: 'the Community and your Circle', short: 'Community' },
  zw: { name: 'Zodiac Wheel', h: 285, room: 'the Sun’s seal', short: 'Studio' },
  mo: { name: 'The Moon', h: 258, room: 'The Chorus', short: 'Chorus' },
  su: { name: 'The Sun', h: 270, room: 'your Studio', short: 'Studio' },
  vo: { name: 'The Chorus', h: 250, room: 'the Voice', short: 'Voice' },
  sa: { name: 'Saturn', h: 225, room: '', short: '' },
}
/** The server's keys (long or short) → the drawing's. */
const KEY = {
  fs: 'fs', first_star: 'fs', the_first_star: 'fs',
  ri: 'ri', rising: 'ri', the_rising: 'ri',
  ve: 've', venus: 've',
  zw: 'zw', zodiac: 'zw', zodiac_wheel: 'zw', the_zodiac_wheel: 'zw',
  mo: 'mo', moon: 'mo', the_moon: 'mo',
  su: 'su', sun: 'su', the_sun: 'su',
  vo: 'vo', chorus: 'vo', the_chorus: 'vo', voice: 'vo', the_voice: 'vo',
  sa: 'sa', saturn: 'sa',
}
export const sectionKey = (k) => KEY[String(k || '').toLowerCase()] || null

// Where each star sits in its section, by index: [x, y from the section's top, label side].
const SLOTS = {
  fs: [[70, 148, 'b'], [180, 170, 'b'], [290, 148, 'b']],
  ri: [[160, 104, 'b'], [62, 162, 'b'], [272, 78, 'b']],
  ve: [[140, 92, 'l'], [220, 92, 'r'], [180, 200, 'b']],
  zw: [[110.7, 88, 'l'], [249.3, 88, 'r'], [180, 208, 'b']],
  // The Moon's nights, an arc under the disc: crescent, half, full.
  mo: [[96, 98, 'l'], [180, 186, 'b'], [264, 98, 'r']],
  su: [[96, 130, 'l'], [180, 54, 'r'], [264, 130, 'r'], [180, 206, 'r']],
  // The Chorus: a small seven-star cluster (prototype revision 5).
  vo: [[110, 168, 'b'], [180, 92, 'b'], [250, 168, 'b']],
}
const MOON_PHASES = ['crescent', 'half', 'full']
// Decorative nodes: [x, y, owner] (a star index lights it; 'ALL' only when drawn).
const DECOR = {
  fs: { C: [180, 76, 'ALL'] },
  ve: { t: [180, 52, 'ALL'], ur: [208.3, 63.7, 1], lr: [208.3, 120.3, 1], b: [180, 132, 2], ll: [151.7, 120.3, 0], ul: [151.7, 63.7, 0], m: [180, 168, 2], cl: [154, 168, 2], cr: [206, 168, 2] },
  mo: { top: [180, 42, 'ALL'] },
  vo: { a: [142, 128, 0], b: [218, 128, 2], c: [180, 140, 'ALL'], d: [160, 200, 'ALL'], e: [205, 205, 'ALL'], f: [180, 56, 1] },
}
const EDGES = {
  fs: [[0, 'C'], [1, 'C'], [2, 'C'], [0, 1], [1, 2]],
  ri: [[1, 0], [0, 2]],
  ve: [['t', 'ur'], ['ur', 1], [1, 'lr'], ['lr', 'b'], ['b', 'll'], ['ll', 0], [0, 'ul'], ['ul', 't'], ['b', 'm'], ['m', 2], ['cl', 'm'], ['m', 'cr']],
  zw: [[0, 1], [1, 2], [2, 0]],
  mo: [[0, 'top'], ['top', 2], [0, 1], [1, 2]],
  su: [],
  vo: [[0, 'a'], ['a', 1], [1, 'b'], ['b', 2]],
}
const COMING = ['Syndication', 'Revenue share', 'Scheduling', 'Video readings', 'Merch']
const SIGNS = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces']
/** "Pisces" → 11 (the prototype's wheel order), -1 when unknown. */
export const signIndex = (s) => SIGNS.indexOf(String(s || '').trim().toLowerCase())

// ── colours (the prototype's) ─────────────────────────────────────────────
const GOLD = '#f0d9a3'; const GOLDHI = '#ffe7b5'; const LIL = '#9b86c9'; const LILLT = '#cbb8ee'; const DIM = '#8e7bb5'; const INK2 = '#e6dafa'
export const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
export function sparkle (cx, cy, r) {
  const a = r * 0.27
  return `M${cx} ${cy - r} L${cx + a} ${cy - a} L${cx + r} ${cy} L${cx + a} ${cy + a} L${cx} ${cy + r} L${cx - a} ${cy + a} L${cx - r} ${cy} L${cx - a} ${cy - a} Z`
}

/** A label in short lines (the prototype's breaks, by width). */
export function wrapLabel (text, max = 14) {
  const out = []
  for (const w of String(text || '').split(/\s+/).filter(Boolean)) {
    const last = out.length ? out[out.length - 1] : null
    if (last != null && (last + ' ' + w).length <= max) out[out.length - 1] = last + ' ' + w
    else out.push(w)
  }
  return out
}

// ── reading the server's sky (API.md v1 §3) ───────────────────────────────
const STATUS = { drawn: 'done', done: 'done', current: 'cur', open: 'cur', cur: 'cur', sealed: 'sealed' }
const action = (b) => (b && b.kind && b.kind !== 'none' ? { ...b } : null)

// Astrology only: the moment never names the card ("… and drew **The Fool**.").
function astrologyOnly (m) {
  const out = { ...m }
  if (m.card && typeof m.line === 'string') out.line = m.line.replace(/,? and drew \*\*[^*]+\*\*/g, '')
  delete out.card
  return out
}
const cardless = (word, moment) => (moment && moment.card && moment.card.name && word === moment.card.name ? '' : word)

function readStar (s, i, sec, { along = false } = {}) {
  const done = !!s.done
  const ask = s.ask || {}
  const out = {
    key: s.key, label: s.label || '', done, along, gates: s.gates !== false,
    // Astrology only: a card's name is never a star's word.
    word: done ? cardless(String(s.word || (s.moment && s.moment.word) || '').trim(), s.moment) : '',
    moment: done && s.moment ? astrologyOnly(s.moment) : null,
    sub: !done ? (s.sub || '') : '',
    body: ask.body || '', button: action(ask.button), quiet: action(ask.quiet), later: ask.later || '',
    ring: s.ring && s.ring.who ? { ...s.ring } : null,
    payout: s.payout ?? null, claimable: !!s.claimable, claimed: !!s.claimed, questKeys: Array.isArray(s.quest_keys) ? s.quest_keys.slice() : [],
    lines: Array.isArray(s.lines) && s.lines.length ? s.lines.slice() : null,
  }
  if (!along) {
    const slot = (SLOTS[sec] || [])[i] || [180, 130, 'b']
    out.x = slot[0]; out.y = slot[1]; out.pos = slot[2]
    // Wrap to the room the label really has (a right label near the edge is narrow).
    if (!out.lines) {
      const room = slot[2] === 'b' ? 160 : slot[2] === 'r' ? SKY_W - slot[0] - 27 : slot[0] - 27
      out.lines = wrapLabel(out.label, Math.max(8, Math.min(slot[2] === 'b' ? 16 : 14, Math.floor(room / 7.4))))
    }
    if (sec === 'mo') out.phase = MOON_PHASES[i] || 'full'
  }
  return out
}

/**
 * The server's `sky` as the app's one shape; null when there is none.
 * opts: what the sky doesn't carry (the round portrait, their Sun sign).
 */
export function readJourney (sky, { portrait = '', sunSign = '' } = {}) {
  if (!sky || !Array.isArray(sky.constellations) || !sky.constellations.length) return null
  const sections = {}
  const order = []
  for (const k of SECTION_ORDER) sections[k] = { key: k, apiKey: k, ...SEC[k], status: k === 'sa' ? 'sa' : 'fut', stars: [], along: [], line: '', seal: null, coming: k === 'sa' ? COMING.slice() : [] }
  for (const c of sky.constellations) {
    const k = sectionKey(c.key)
    if (!k) continue
    const s = sections[k]
    if (!order.includes(k)) order.push(k)
    s.apiKey = c.key
    if (c.name) s.name = c.name
    if (c.room && typeof c.room === 'object') { s.room = c.room.name || s.room; s.short = c.room.short || s.short } else if (typeof c.room === 'string' && c.room) s.room = c.room
    s.status = k === 'sa' ? 'sa' : (STATUS[c.state] || 'fut')
    s.stars = (c.stars || []).filter((x) => !x.hidden).map((x, i) => readStar(x, i, k))
    s.along = (c.along || []).filter((x) => !x.hidden).map((x, i) => readStar(x, i, k, { along: true }))
    if (c.seal) s.seal = { ...c.seal }
    s.reward = c.reward && typeof c.reward === 'object' ? { total: Number(c.reward.total) || 0, eachNight: Number(c.reward.each_night) || 0 } : null
    s.complete = s.stars.every((x) => x.done)
    if (Array.isArray(c.coming) && c.coming.length) s.coming = c.coming.slice()
  }
  for (const k of SECTION_ORDER) if (sections[k].complete === undefined) sections[k].complete = false
  // The server's order (its `order` field, else as listed); Saturn always last.
  const pos = {}
  for (const c of sky.constellations) { const k = sectionKey(c.key); if (k && pos[k] === undefined) pos[k] = Number.isFinite(Number(c.order)) ? Number(c.order) : order.indexOf(k) }
  order.sort((a, b) => (a === 'sa') - (b === 'sa') || pos[a] - pos[b])
  // The seal broken and the Studio not yet taken: the Sun is the one in
  // progress, whatever state the sky gives it (the server says "drawn").
  const sealWaits = !!(sections.su.seal && sections.su.seal.broken && !sections.su.seal.claimed)
  if (sealWaits) sections.su.status = 'cur'
  // Every constellation drawn is not the end while the Sun's own steps wait:
  // the Sun stays the one in progress until they are lit (prototype "The Sun, partway").
  const allDrawn = !order.filter((k) => k !== 'sa').some((k) => sections[k].status !== 'done')
  const sunSteps = allDrawn && !sections.su.complete && sections.su.stars.length > 0
  const finished = !sealWaits && !sunSteps && allDrawn && !sky.current
  let cur = finished ? null : (sealWaits || (sunSteps && !sky.current) ? 'su' : sectionKey(sky.current))
  if (!cur && !finished) cur = order.find((k) => sections[k].status === 'cur') || null
  // The Sun stays under its seal until it breaks; its line once the level
  // before it (the Zodiac Wheel since revision 5; the Moon before) is under way.
  const su = sections.su
  const broken = !!(su.seal && su.seal.broken)
  if (su.status !== 'done' && !broken) su.status = 'sealed'
  const beforeSun = order[order.indexOf('su') - 1] || ''
  su.line = su.status === 'sealed' && su.seal && su.seal.line && beforeSun && (cur === beforeSun || sections[beforeSun].status === 'done') ? su.seal.line : ''
  const yh = sky.you_are_here
  const here = finished ? null : (typeof yh === 'string' ? yh : (yh && (yh.star || yh.key)) || (sky.next && sky.next.star) || null)
  const money = !!sky.carats_visible
  const w = sky.welcome_claim
  const ca = sky.claim_all
  return {
    sections, order, cur, here: here === 'seal' ? null : here, finished,
    daily: sky.daily && typeof sky.daily === 'object' ? { ...sky.daily } : null,
    // sky v3 (BE-CONTRACT §2): tonight's star, from the seal breaking until cast; undefined from an older server.
    tonight: 'tonight_vote' in sky ? (sky.tonight_vote ? { ...sky.tonight_vote } : null) : undefined,
    // The Sun's seal is broken and not yet taken: its card waits in the Quest.
    unseal: sealWaits ? { title: su.seal.title || 'The Studio is open', sub: su.seal.sub || '', button: action(su.seal.button) } : null,
    next: sky.next ? { ...sky.next } : null,
    portrait,
    // Their Sun sign: given, else the birthday star's word when it names a sign.
    sunSign: sunSign || (sections.ri.stars[0] && signIndex(sections.ri.stars[0].word) >= 0 ? sections.ri.stars[0].word : ''),
    money,
    welcome: money && w && Number(w.total) > 0 ? { total: Number(w.total), title: w.title || 'Your stars have been saving for you', label: (w.button && w.button.label) || 'Claim' } : null,
    claim: money && !w && ca && Number(ca.total) > 0 ? { total: Number(ca.total), count: Number(ca.count) || 0 } : null,
    studio: money && sky.studio ? { ...sky.studio } : null,
    giftStar: giftStarOf(sky, money),
    unlock: sky.unlock || null,
    fresh: Array.isArray(sky.fresh) ? sky.fresh.slice() : [],
    rings: order.flatMap((k) => sections[k].stars.filter((x) => x.ring && x.ring.fresh).map((x) => ({ star: x.key, word: `${x.ring.who}${x.ring.word ? ' · ' + x.ring.word : ''}` }))),
  }
}

/**
 * The gift-of-the-day star (mystic-page 2026-10-29, API.md §10): the daily
 * row, only once money shows (the Community is open). Collected through the
 * existing Claim (it is in claim_all), never a button of its own.
 */
export const GIFT_STAR_SEEN_KEY = 'ma_gift_star_landed'
export function giftStarOf (sky, money) {
  const g = sky && sky.daily && sky.daily.gift_star
  if (!money || !g || !g.state) return null
  return { state: g.state, carats: Number(g.carats) || 0, title: g.title || '', line: g.line || '', litAt: g.lit_at || '' }
}

/** The levels in the server's order, bottom up (Saturn last). */
export function levelOrder (j) {
  return j && Array.isArray(j.order) && j.order.length ? j.order : SECTION_ORDER
}

/** Where a star (or an along-the-way star) lives. */
export function findStar (j, key) {
  if (!j) return null
  for (const k of levelOrder(j)) {
    const s = j.sections[k]
    const hit = s.stars.find((x) => x.key === key) || s.along.find((x) => x.key === key)
    if (hit) return { sec: s, star: hit }
  }
  return null
}

/** How full the Moon is: a third for each night; drawn, it is full. */
export function moonFraction (sec) {
  if (!sec) return 0
  if (sec.status === 'done') return 1
  if (sec.status === 'fut' || !sec.stars.length) return 0
  return Math.min(1, sec.stars.filter((s) => s.done).length / 3)
}

// ── the three questions ───────────────────────────────────────────────────
/** What's next: the eyebrow names the section ("The Moon · Next"). */
export function nextCard (j) {
  if (!j) return null
  if (j.finished) {
    if (!j.next || !j.next.title) return null
    return { section: '', eyebrow: j.next.eyebrow || '', title: j.next.title, body: j.next.body || '', button: btn(j.next.button), quiet: btn(j.next.quiet), later: j.next.later || '', star: null }
  }
  if (j.unseal || (j.cur === 'su' && j.sections.su.status === 'sealed')) return null
  const n = j.next || {}
  if (n.star === 'seal') return null
  const hit = findStar(j, n.star || j.here)
  if (!hit && !n.title) return null
  const st = hit ? hit.star : {}
  const secKey = sectionKey(n.constellation) || (hit && hit.sec.key) || j.cur
  return {
    section: n.eyebrow ? String(n.eyebrow).replace(/\s*·\s*Next$/i, '') : (secKey ? j.sections[secKey].name : ''),
    title: n.title || st.label || '',
    body: n.body || st.body || '',
    button: btn(n.button !== undefined ? n.button : st.button),
    quiet: btn(n.quiet !== undefined ? n.quiet : st.quiet),
    later: n.later || st.later || '',
    star: st.key || n.star || null,
  }
}
function btn (b) { return b && b.kind && b.kind !== 'none' && b.label ? { ...b } : null }

/** Where am I: seven glyphs, the one in progress ringed. */
export function stripItems (j) {
  const items = levelOrder(j).map((k) => {
    const s = j.sections[k]
    const cls = s.status === 'done' ? 'done' : (j.cur === k && !j.finished) ? 'cur' : 'fut'
    const seed = cls === 'fut' && s.stars.some((x) => x.done)
    return { key: k, name: s.name, cls, on: cls !== 'fut', seed }
  })
  return { items, here: j.cur && !j.finished ? j.sections[j.cur].name : '' }
}

/** The ordered list: a line per section; the current one unfolds. */
export function listRows (j) {
  const rows = []
  for (const k of levelOrder(j)) {
    const s = j.sections[k]
    const cur = j.cur === k && !j.finished
    let line = ''
    if (s.status === 'done') line = `✓ ${s.short}`
    else if (k === 'sa') line = 'coming'
    else if (k === 'su' && s.status === 'sealed' && s.line) line = s.line
    rows.push({ type: 'section', key: k, name: s.name, line, on: s.status === 'done' || cur, fut: !(s.status === 'done' || cur) })
    if (cur && s.status !== 'sealed') {
      for (const st of s.stars) rows.push({ type: 'star', key: st.key, label: st.label, mark: st.done ? '✓' : j.here === st.key ? '◆' : '○', word: st.word, here: j.here === st.key })
      if (s.along.length) {
        rows.push({ type: 'sub', key: `${k}-along`, label: 'Along the way' })
        for (const st of s.along) rows.push({ type: 'along', key: st.key, label: st.label, mark: st.done ? '✓' : '·' })
      }
    }
  }
  return rows
}

/** A tapped star's sheet. */
export function starSheet (j, key) {
  if (!j) return null
  if (key === 'saturn' || key === 'sa') return { key: 'saturn', section: 'Saturn', title: 'Coming', coming: j.sections.sa.coming, lit: false, button: null }
  const hit = findStar(j, key)
  if (!hit) return null
  const { sec, star } = hit
  const lit = star.done || (sec.status === 'done' && sec.complete)
  const out = { key, section: sec.name, along: star.along, lit, title: `${lit ? '✓ ' : ''}${star.label}`, moment: lit ? star.moment : null,
    body: lit ? '' : star.body, sub: star.sub || '', button: null, quiet: null, ring: star.ring,
    claim: j.money && star.claimable && star.questKeys.length ? { payout: star.payout, key: star.questKeys[0] } : null }
  // A star in progress, or one along the way, or the letter once written: its button.
  if (!lit && (sec.status === 'cur' || sec.status === 'done' || star.along)) { out.button = btn(star.button); out.quiet = btn(star.quiet) }
  if (lit && key === 'aw_letter') out.button = btn(star.button) || { kind: 'letter', label: 'Read the letter' }
  return out
}

// ── one level in focus (recipient polish 2026-11-03, DESIGN §18, §20.5, §20.6) ──
// What each level opens, in plain words (the unlock map, §12), and the tab it
// brings (its icon in the panel); none for the levels that open no tab.
const LEVEL = {
  fs: { unlock: 'Your Journal', verb: 'open your Journal', tab: 'journal' },
  ri: { unlock: 'Your Altar, with your Mirror inside', verb: 'open your Altar', tab: 'altar' },
  ve: { unlock: 'The Community and your Circle', verb: 'open the Community and your Circle', tab: 'community' },
  zw: { unlock: 'Breaks the Sun’s seal: your Studio', verb: 'break the Sun’s seal', tab: 'studio' },
  su: { unlock: 'Your Studio’s four steps', verb: 'finish your Studio', tab: '' },
  mo: { unlock: 'Opens The Chorus, the Voice’s constellation', verb: 'open The Chorus', tab: '' },
  vo: { unlock: 'Your whole sky, drawn', verb: 'draw your whole sky', tab: '' },
}
/** The banner when a level's last star lights: what opened. */
const BANNER = {
  fs: { eyebrow: 'First Star is drawn', title: 'Your Journal is open', text: 'Every reading you keep lives there.' },
  ri: { eyebrow: 'The Rising is drawn', title: 'Your Altar is open', text: 'Home and your Mirror are inside, beside your Journal.' },
  ve: { eyebrow: 'Venus is drawn', title: 'The Community and your Circle', text: 'Your Circle is inside your Altar, in your Mirror.' },
  zw: { eyebrow: 'Zodiac Wheel is drawn', title: 'The Sun’s seal breaks', text: 'Your Studio is waiting in The Sun.' },
  su: { eyebrow: 'The Sun is drawn', title: 'Your Mystic is made', text: 'Next, The Moon: come back on three nights.' },
  mo: { eyebrow: 'The Moon is full', title: 'The Chorus opens', text: 'The Voice’s own constellation: help choose what comes next.' },
  vo: { eyebrow: 'The Chorus is drawn', title: 'Your sky is drawn', text: 'A reading made for someone is always open.' },
}
const TONIGHT_BODY = 'One short question each night about what Mystic AIda makes next. Pick an answer and it counts.'

/** The level the Quest opens on: the one in progress. */
export function focusDefault (j) {
  if (!j) return 'fs'
  const order = levelOrder(j)
  if (j.cur && order.includes(j.cur)) return j.cur
  if (j.finished) return 'sa'
  return order.find((k) => k !== 'sa' && j.sections[k].status !== 'done') || order[0] || 'fs'
}

// "the Zodiac Wheel", but "First Star", "The Rising", "Venus".
const withThe = (name) => (/^(zodiac wheel)$/i.test(name) ? `the ${name}` : name)
const isOpen = (sec) => sec.status === 'cur' || sec.status === 'done'
const sumPayout = (sec) => sec.stars.reduce((a, st) => a + (Number(st.payout) || 0), 0)
// Money shows from the Zodiac Wheel on, once the Community is open (§18, §20.6).
function pays (j, k) {
  const order = levelOrder(j)
  const zw = order.indexOf('zw')
  return !!j.money && order.indexOf(k) >= (zw >= 0 ? zw : 3)
}
const nightPot = (j) => Number(j.daily && (j.daily.pot || j.daily.next_amount)) || 0

/**
 * The panel for the focused level: its place, one line, what it unlocks,
 * the reward (from the Zodiac Wheel on), the steps numbered like the stars,
 * the along the way, and the levels either side.
 */
export function levelPanel (j, key) {
  if (!j) return null
  const k = sectionKey(key) || focusDefault(j)
  const order = levelOrder(j)
  const i = order.indexOf(k)
  const sec = j.sections[k]
  const near = (n) => (n >= 0 && n < order.length ? { key: order[n], name: j.sections[order[n]].name } : null)
  const out = { key: k, name: sec.name, eyebrow: `Level ${i + 1} of ${order.length} · ${sec.name}`, prev: near(i - 1), next: near(i + 1) }
  if (k === 'sa') return { ...out, teaser: true, line: 'Coming', coming: sec.coming.slice(), drawn: !!j.finished }
  const L = LEVEL[k] || { unlock: sec.room, verb: `open ${sec.room}`, tab: '' }
  const n = sec.stars.length
  const lit = sec.stars.filter((st) => st.done).length
  const drawn = sec.status === 'done' && (sec.complete || !n)
  const prev = out.prev ? withThe(out.prev.name) : ''
  let line
  if (drawn) line = '✓ Every star is lit.'
  else if (sec.status === 'sealed') line = `Sealed until ${prev || 'the level before'} is lit.`
  else if (sec.status === 'fut') line = prev ? `Opens once ${prev} is fully lit.` : ''
  else line = `Light ${n === 2 ? 'both' : n === 1 ? 'its' : `all ${n}`} star${n === 1 ? '' : 's'} to ${L.verb}.`
  const money = pays(j, k)
  const pot = nightPot(j)
  let reward = ''
  const rw = sec.reward
  const each = (rw && rw.eachNight) || (k === 'mo' ? pot : 0)
  const total = (rw && rw.total) || sumPayout(sec)
  if (money && k === 'mo') reward = each ? `Each night you come back: ✦ ${each}.` : ''
  else if (money && total > 0) reward = `Reward: ✦ ${total} when every star is lit.`
  const avail = isOpen(sec)
  const nextKey = avail ? ((sec.stars.find((st) => st.key === j.here && !st.done) || sec.stars.find((st) => !st.done && st.gates) || sec.stars.find((st) => !st.done) || {}).key || null) : null
  const row = (st, along) => {
    const coin = money ? (k === 'mo' && !along ? (pot ? `✦ ${pot}` : '') : (Number(st.payout) > 0 ? `✦ ${st.payout}` : '')) : ''
    const r = { key: st.key, label: st.label, along, state: st.done ? 'lit' : (!along && st.key === nextKey ? 'next' : 'not'),
      word: st.done ? (st.word || 'done') : '', body: st.done ? '' : st.body, sub: st.sub || '', coin, button: null, quiet: null, later: '', notYet: false, moment: !!(st.done && st.moment) }
    if (!st.done) {
      if (avail || along) { r.button = btn(st.button); r.quiet = btn(st.quiet); r.later = st.later || '' } else if (st.later) r.later = st.later; else r.notYet = true
    }
    return r
  }
  return {
    ...out, line, drawn, tab: L.tab,
    unlock: { text: L.unlock, state: drawn ? '✓ Open' : `${lit} of ${n} lit`, open: drawn },
    reward,
    steps: sec.stars.map((st) => row(st, false)),
    along: (avail || drawn) ? sec.along.map((st) => row(st, true)) : [],
  }
}

/** "Tonight · Cast your star" above the panel, every night until cast, once the Studio is open. */
export function tonightCard (j) {
  if (!j || !j.money) return null
  if (j.tonight !== undefined) {
    const t = j.tonight
    if (!t || !t.button) return null
    const amt = Number(t.payout) || 0
    return { eyebrow: t.eyebrow || 'Tonight · The Voice', title: t.title || 'Cast your star', body: t.body || TONIGHT_BODY,
      button: { ...t.button, label: amt ? `${t.button.label || 'Cast my star'} · ✦ ${amt}` : (t.button.label || 'Cast my star') } }
  }
  if (!j.daily || !j.daily.vote || j.daily.vote.done_today) return null
  const su = j.sections.su
  const studio = su.status === 'done' || !!(su.seal && su.seal.broken && su.seal.claimed)
  if (!studio) return null
  const amt = Number(j.daily.vote.payout) || 0
  return { eyebrow: 'Tonight · The Voice', title: 'Cast your star', body: TONIGHT_BODY, button: { kind: 'voice', label: amt ? `Cast my star · ✦ ${amt}` : 'Cast my star' } }
}

/** What a level's completion says, and the carats it paid (once money shows). */
export function levelBanner (j, key) {
  const k = sectionKey(key)
  const b = BANNER[k]
  if (!b || !j) return null
  const sec = j.sections[k]
  return { key: k, ...b, carats: pays(j, k) && k !== 'mo' ? ((sec.reward && sec.reward.total) || sumPayout(sec)) : 0 }
}

/** What a button does in the app; ReaderPage carries it out. */
export function actionFor (button) {
  if (!button || !button.kind || button.kind === 'none') return null
  switch (button.kind) {
    case 'draw': return { type: 'draw', reader: button.reader || 'iam' }
    case 'birthday': return { type: 'birthday' }
    case 'birth_time': return { type: 'birthday', prefill: true }
    case 'birth_time_unknown': return { type: 'act', act: 'birth_time_unknown' }
    case 'act': return button.act ? { type: 'act', act: button.act } : null
    case 'keep_sky': return { type: 'keep-sky' }
    case 'mirror': case 'mirror_name': case 'mirror_avatar': return { type: 'mirror' }
    case 'conjurings': return { type: 'mirror', tab: 'conjurings' }
    case 'conjure': return { type: 'conjure' }
    case 'claim_all': return { type: 'claim-all' }
    case 'share_reading': return { type: 'quest', category: 'share_reading' }
    case 'thank':
      // Thank the Mystic of their own reading (Mystic AIda counts): asked in the Quest.
      if (button.mystic && button.reading) return { type: 'thank-mystic', reading: button.reading, reader: button.reader || '' }
      // A gift's thank opens its room at the reply card, step 4 (funnel gap 6).
      if (button.reading && button.inv) return { type: 'reading', reading: button.reading, reader: button.reader || '', inv: button.inv, step: 4 }
      return button.reading ? { type: 'reading', reading: button.reading, reader: button.reader || '', inv: button.inv || '' } : { type: 'journal' }
    case 'reading':
      if (!button.reading) return { type: 'journal' }
      // Say what landed (fix 2026-10-30): the line is asked right in the Quest.
      if (button.write === 'landed') return { type: 'landed', reading: button.reading, reader: button.reader || '', inv: button.inv || '' }
      // Rate / talk: the reading asks "What landed?" when it opens without one.
      // Unrated: after its rating (the ask holds the rating back); rated: on open.
      return { type: 'reading', reading: button.reading, reader: button.reader || '', inv: button.inv || '', askLanded: !!button.ask_landed, rated: !!button.rated }
    case 'journal': return { type: 'journal' }
    case 'circle': case 'invite': return { type: 'circle' }
    case 'send_gift': return { type: 'send-gift' }
    case 'lessons': return { type: 'lessons' }
    case 'lesson': return (button.id || button.short) ? { type: 'lesson', short: button.id || button.short } : { type: 'lessons' }
    case 'voice': return { type: 'voice' }
    case 'community': return { type: 'community' }
    case 'altar': return { type: 'altar' }
    case 'letter': return { type: 'letter' }
    case 'open_studio': return { type: 'claim-studio' }
    case 'studio': return { type: 'studio', view: button.view || '' }
    case 'share_mystic': return { type: 'studio', view: 'share' }
    default: return null
  }
}

/** The reading a Quest verb opens that still waits for a "What landed?" line, or ''. */
export function landedAskFor (verb) {
  return verb && verb.type === 'reading' && verb.askLanded && verb.reading ? String(verb.reading) : ''
}

// ── the moment's text ("**Ellis** read for you: *…*") ─────────────────────
export function momentSegments (text = '') {
  return String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/).filter(Boolean).map((t) => {
    if (t.startsWith('**') && t.endsWith('**') && t.length > 4) return { text: t.slice(2, -2), b: true, i: false }
    if (t.startsWith('*') && t.endsWith('*') && t.length > 2) return { text: t.slice(1, -1), b: false, i: true }
    return { text: t, b: false, i: false }
  })
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** "2026-10-20" → "Mon 20 Oct" (the moment's date). */
export function momentDay (iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  if (!m) return ''
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

// ── layout and drawing (prototype.html drawSky, ported) ───────────────────
export const SKY_W = 360
export function skyLayout (j = null) {
  const top = {}
  let y = 14
  for (const k of levelOrder(j).slice().reverse()) { top[k] = y; y += SEC[k].h }
  return { top, H: y + 26 }
}
/** Where a section's middle sits (the view centres there). */
export function sectionCentre (k, j = null) {
  const { top } = skyLayout(j)
  if (j && j.finished) return top.su + 60
  const key = sectionKey(k) || 'su'
  return top[key] + SEC[key].h * 0.5
}

const GLYPH_FONT = 'font-family="\'Apple Symbols\',\'Segoe UI Symbol\',\'Noto Sans Symbols 2\',\'DejaVu Sans\',sans-serif"'
export const GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((g) => g + '︎')

function defs (p) {
  return `<defs>
  <filter id="${p}glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
  <radialGradient id="${p}halo"><stop offset="0" stop-color="#ffe7b5" stop-opacity=".6"/><stop offset="1" stop-color="#c9a94e" stop-opacity="0"/></radialGradient>
  <radialGradient id="${p}moonFill" cx=".4" cy=".35" r=".75"><stop offset="0" stop-color="#fffaf0"/><stop offset=".7" stop-color="#f0e2bd"/><stop offset="1" stop-color="#cdb98a"/></radialGradient>
  <radialGradient id="${p}sunFill" cx=".45" cy=".4" r=".7"><stop offset="0" stop-color="#fff6dc"/><stop offset=".6" stop-color="#ffd98a"/><stop offset="1" stop-color="#e0a84a"/></radialGradient>
  <clipPath id="${p}pclip"><circle cx="0" cy="0" r="18"/></clipPath>
</defs>`
}

/**
 * The whole sky as one SVG string. fx: { bright, ceremony, ring, liftWord },
 * pfx: an id prefix (two skies can share a page). Server words are escaped.
 */
export function drawSky (j, fx = {}, pfx = 'qj') {
  const { top, H } = skyLayout(j)
  const p = `${pfx}-`
  let s = ''
  let seed = 11
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280
  for (let i = 0; i < Math.floor(H / 8); i++) s += `<circle cx="${(rnd() * SKY_W).toFixed(1)}" cy="${(rnd() * H).toFixed(1)}" r="${(rnd() * 0.9 + 0.3).toFixed(2)}" fill="#efe4ff" opacity="${(rnd() * 0.3 + 0.07).toFixed(2)}"/>`
  for (const k of levelOrder(j).slice().reverse()) s += drawBlock(j, k, top[k], fx, p)
  s += `<text x="${SKY_W / 2}" y="${H - 8}" text-anchor="middle" class="it-s" font-size="13" fill="${DIM}">Only you see your sky.</text>`
  return `<svg class="sky" viewBox="0 0 ${SKY_W} ${H}" role="img" aria-label="Your sky">${defs(p)}${s}</svg>`
}

function lit (sec, idx) {
  if (sec.status === 'done') return true
  if (typeof idx === 'number') return !!(sec.stars[idx] && sec.stars[idx].done)
  const d = DECOR[sec.key][idx]
  if (d[2] === 'ALL') return false
  return !!(sec.stars[d[2]] && sec.stars[d[2]].done)
}
function posOf (sec, idx) {
  if (typeof idx === 'number') { const slot = (SLOTS[sec.key] || [])[idx]; return slot ? [slot[0], slot[1]] : null }
  const d = DECOR[sec.key] && DECOR[sec.key][idx]
  return d ? [d[0], d[1]] : null
}

function drawBlock (j, k, y0, fx, p) {
  const sec = j.sections[k]
  const status = k === 'su' && fx.ceremony ? 'sealed' : sec.status
  const on = status === 'done' || status === 'cur' || (k === 'su' && fx.ceremony)
  const bright = fx.bright && status === 'done'
  let s = `<g class="blk${bright ? ' bright' : ''}" data-sec="${k}" style="--d:${bright ? (levelOrder(j).indexOf(k) * 0.16 + 3.9) + 's' : '0s'}" transform="translate(0 ${y0})">`
  s += `<text x="22" y="28" class="dsp" font-size="11" letter-spacing="2.2" fill="${on ? GOLD : LIL}" opacity="${on ? 1 : (status === 'sa' ? 0.55 : 0.5)}">${esc(sec.name.toUpperCase())}</text>`
  if (status === 'done' && sec.room) s += `<text x="22" y="46" class="it-s" font-size="14.5" fill="${INK2}">${esc(sec.room)}</text>`
  if (k === 'sa') return s + drawSaturn(sec) + '</g>'
  if (k === 'su' && status === 'sealed') return s + drawSeal(j, sec, fx, p) + '</g>'
  s += drawBackdrop(j, sec, status, p)
  for (const [a, b] of EDGES[k]) {
    const A = posOf(sec, a); const B = posOf(sec, b)
    if (!A || !B) continue
    s += edge(A, B, status, lit(sec, a) && lit(sec, b))
  }
  if (k === 'su') s += sunRays(sec, status)
  if (DECOR[k]) {
    for (const [key, d] of Object.entries(DECOR[k])) {
      if (k === 'fs' && key === 'C') { s += firstStar(d, status, p); continue }
      const l = lit(sec, key)
      s += `<circle cx="${d[0]}" cy="${d[1]}" r="${l ? 2.4 : 1.6}" fill="${l ? GOLD : LIL}" opacity="${status === 'fut' ? 0.35 : (l ? 1 : 0.6)}"/>`
    }
  }
  // A drawn constellation's unlit stars stay labelled (API §3 `complete`).
  const starStatus = status === 'done' && !sec.complete ? 'open' : status
  sec.stars.forEach((st) => { s += drawStar(j, sec, st, starStatus, fx, p) })
  if (sec.along.length && on && !fx.noLabels) {
    const n = sec.along.length
    const yb = SEC[k].h - 16
    s += `<text x="336" y="${yb - n * 20 - 2}" text-anchor="end" class="dsp" font-size="7.5" letter-spacing="1.3" fill="${LIL}" opacity=".85">ALONG THE WAY</text>`
    sec.along.forEach((a, i) => {
      const y = yb - (n - 1 - i) * 20
      const l = a.done
      s += `<g class="st" data-key="${esc(a.key)}" role="button" tabindex="0" aria-label="${esc(a.label)}${l ? ', done' : ''}">` +
        `<rect class="hit" x="150" y="${y - 12}" width="200" height="20" fill="transparent"/>` +
        `<path d="${sparkle(342, y - 4, l ? 5 : 3.6)}" fill="${l ? GOLDHI : LILLT}" opacity="${l ? 1 : 0.6}"/>` +
        `<text x="333" y="${y}" text-anchor="end" class="it-s" font-size="13.5" fill="${l ? INK2 : DIM}">${l ? '✓ ' : ''}${esc(a.label)}</text></g>`
    })
  }
  return s + '</g>'
}

function edge (A, B, status, gold) {
  if (status === 'done' || gold) return `<line x1="${A[0]}" y1="${A[1]}" x2="${B[0]}" y2="${B[1]}" stroke="${GOLD}" stroke-width="${status === 'done' ? 1.25 : 1.4}" opacity=".95"/>`
  return `<line x1="${A[0]}" y1="${A[1]}" x2="${B[0]}" y2="${B[1]}" stroke="${LIL}" stroke-width=".8" stroke-dasharray="1.5 4" opacity="${status === 'fut' ? 0.3 : 0.6}"/>`
}

function firstStar (d, status, p) {
  const [x, y] = d
  if (status !== 'done') return `<circle cx="${x}" cy="${y}" r="2" fill="${LIL}" opacity="${status === 'fut' ? 0.35 : 0.6}"/>`
  return `<circle cx="${x}" cy="${y}" r="30" fill="url(#${p}halo)" opacity=".55"/><g stroke="${GOLDHI}" stroke-width=".8" opacity=".8"><line x1="${x}" y1="${y - 26}" x2="${x}" y2="${y + 26}"/><line x1="${x - 26}" y1="${y}" x2="${x + 26}" y2="${y}"/></g><path d="${sparkle(x, y, 12)}" fill="${GOLDHI}" filter="url(#${p}glow)"/>`
}

function drawBackdrop (j, sec, status, p) {
  const done = status === 'done'; const fut = status === 'fut'
  const k = sec.key
  let s = ''
  if (k === 'ri') {
    s += `<line x1="22" y1="162" x2="338" y2="162" stroke="${done ? GOLD : LIL}" stroke-width="${done ? 1.1 : 0.8}" ${done ? '' : 'stroke-dasharray="3 5"'} opacity="${fut ? 0.3 : (done ? 0.8 : 0.6)}"/>`
    if (done) {
      // The round portrait (circles are seekers) over the horizon.
      s += `<g transform="translate(196 112)"><circle r="23" fill="url(#${p}halo)" opacity=".5"/>` +
        (j.portrait
          ? `<image class="qj-portrait" href="${esc(j.portrait)}" x="-18" y="-18" width="36" height="36" preserveAspectRatio="xMidYMid slice" clip-path="url(#${p}pclip)"/>`
          : `<circle r="18" fill="#2a1850"/><path d="${sparkle(0, 0, 7)}" fill="${GOLDHI}"/>`) +
        `<circle r="18" fill="none" stroke="${GOLD}" stroke-width=".8"/></g>`
    }
  }
  if (k === 'zw') {
    const cx = 180; const cy = 128; const r = 80
    const mine = signIndex(j.sunSign)
    s += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${done ? GOLD : LIL}" stroke-width="${done ? 1 : 0.8}" ${done ? '' : 'stroke-dasharray="1.5 4"'} opacity="${fut ? 0.28 : (done ? 0.7 : 0.55)}"/>`
    for (let i = 0; i < 12; i++) {
      const a = (180 + 30 * i) * Math.PI / 180
      const x = cx + r * Math.cos(a); const y = cy - r * Math.sin(a)
      const gx = cx + (r - 17) * Math.cos(a); const gy = cy - (r - 17) * Math.sin(a)
      const isMine = i === mine
      if (![3, 7, 11].includes(i)) s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${done ? 2 : 1.5}" fill="${done ? GOLD : LIL}" opacity="${fut ? 0.3 : 0.75}"/>`
      if (!fut) s += `<text x="${gx.toFixed(1)}" y="${(gy + 4).toFixed(1)}" text-anchor="middle" ${GLYPH_FONT} font-size="11" fill="${isMine ? GOLDHI : DIM}" opacity="${isMine ? 1 : 0.8}"${isMine ? ` data-mine="${i}"` : ''}>${GLYPHS[i]}</text>`
    }
    if (done) {
      let sd = 5
      const rr = () => (sd = (sd * 9301 + 49297) % 233280) / 233280
      for (let i = 0; i < 46; i++) {
        const a = rr() * Math.PI * 2; const d = r + 10 + rr() * 32
        s += `<circle cx="${(cx + d * Math.cos(a)).toFixed(1)}" cy="${(cy + d * Math.sin(a) * 0.8).toFixed(1)}" r="${(rr() * 0.7 + 0.4).toFixed(2)}" fill="${GOLDHI}" opacity="${(rr() * 0.5 + 0.3).toFixed(2)}"/>`
      }
    }
  }
  if (k === 'mo') {
    const frac = moonFraction({ ...sec, status })
    s += `<circle cx="180" cy="110" r="36" fill="#1b1236" stroke="${done ? GOLD : LIL}" stroke-width=".8" ${done ? '' : 'stroke-dasharray="1.5 4"'} opacity="${fut ? 0.35 : 0.8}"/>`
    if (!fut && frac > 0) {
      s += `<defs><mask id="${p}mPhase"><rect x="0" y="0" width="360" height="260" fill="#fff"/><circle cx="${(180 - frac * 76).toFixed(1)}" cy="110" r="37" fill="#000"/></mask></defs>` +
        `<g mask="url(#${p}mPhase)" data-moon="${frac.toFixed(2)}"><circle cx="180" cy="110" r="36" fill="url(#${p}moonFill)" opacity="${done ? 1 : 0.9}"/></g>`
    }
    if (done) s += `<circle cx="180" cy="110" r="52" fill="url(#${p}halo)" opacity=".45"/>`
  }
  if (k === 'su') {
    s += `<circle cx="180" cy="130" r="24" fill="${done ? `url(#${p}sunFill)` : '#24163f'}" stroke="${GOLD}" stroke-width="${done ? 0 : 0.8}" opacity="${done ? 1 : 0.9}"/>`
    if (done) s += `<circle cx="180" cy="130" r="48" fill="url(#${p}halo)" opacity=".55"/>`
  }
  return s
}

function sunRays (sec, status) {
  let s = ''
  for (let i = 1; i < 8; i += 2) {
    const a = (i * 45) * Math.PI / 180
    s += `<line x1="${(180 + 30 * Math.cos(a)).toFixed(1)}" y1="${(130 + 30 * Math.sin(a)).toFixed(1)}" x2="${(180 + 46 * Math.cos(a)).toFixed(1)}" y2="${(130 + 46 * Math.sin(a)).toFixed(1)}" stroke="${status === 'done' ? GOLD : LIL}" stroke-width=".9" ${status === 'done' ? '' : 'stroke-dasharray="1.5 3"'} opacity=".7"/>`
  }
  SLOTS.su.forEach((slot, i) => {
    const dx = slot[0] - 180; const dy = slot[1] - 130; const d = Math.hypot(dx, dy)
    const l = status === 'done' || !!(sec.stars[i] && sec.stars[i].done)
    s += edge([+(180 + dx / d * 30).toFixed(1), +(130 + dy / d * 30).toFixed(1)], [+(slot[0] - dx / d * 9).toFixed(1), +(slot[1] - dy / d * 9).toFixed(1)], status, l)
  })
  return s
}

function drawStar (j, sec, st, status, fx, p) {
  const { x, y, pos } = st
  const l = st.done || status === 'done'
  const here = j.here === st.key && (status === 'cur' || status === 'open')
  const fut = status === 'fut'
  if (fut && !l) return `<circle cx="${x}" cy="${y}" r="1.8" fill="${LIL}" opacity=".4"/>`
  let g = `<g class="st" data-key="${esc(st.key)}" role="button" tabindex="0" aria-label="${esc(st.label)}${l ? ', done' : here ? ', next' : ''}" transform="translate(${x} ${y})">`
  g += '<circle class="hit" r="18" fill="transparent"/>'
  if (here) g += `<circle r="16" fill="none" stroke="${GOLD}" stroke-width="1" class="pulse"/><circle r="11" fill="rgba(240,217,163,.12)" stroke="${GOLD}" stroke-width=".8"/>`
  g += `<path d="${sparkle(0, 0, l ? (status === 'done' || status === 'open' ? 6.5 : 8) : (here ? 8.5 : 5.5))}" fill="${l || here ? GOLDHI : LILLT}" opacity="${l || here ? 1 : 0.75}" ${l ? `filter="url(#${p}glow)"` : ''}/>`
  if (fx.ring === st.key) g += `<circle class="ringagain" r="10" fill="none" stroke="${GOLDHI}" stroke-width="1.2"/><circle class="ringagain r2" r="10" fill="none" stroke="${GOLDHI}" stroke-width="1"/>`
  if (fx.ringDone === st.key || (st.ring && fx.ring !== st.key)) g += `<circle r="13" fill="none" stroke="${GOLD}" stroke-width=".7" opacity=".7"/>`
  // One level in focus (DESIGN §18): a step number under each star, the words in the panel.
  if (fx.nums) {
    const n = sec.stars.indexOf(st) + 1
    const cy = here ? 27 : 21
    g += `<circle class="num" cx="0" cy="${cy}" r="8" fill="${l ? GOLD : 'rgba(20,10,40,.9)'}" stroke="${l ? GOLD : LILLT}" stroke-width=".8"/>` +
      `<text x="0" y="${cy + 3.6}" text-anchor="middle" font-size="10" font-weight="700" font-family="Inter,sans-serif" fill="${l ? '#1a0a2e' : LILLT}">${n}</text>`
  }
  // Labels on the section in progress (and a lit star waiting in a future one: the gift's Ellis).
  if (!fx.noLabels && (status === 'cur' || status === 'open' || (fut && l))) {
    const all = st.lines.map((t, i) => ({ t: (i === 0 && l ? '✓ ' : '') + t, w: false }))
    if (l && st.word) all.push({ t: st.word, w: true })
    const lh = 15
    let ax; let ay; let anchor
    const off = here ? 23 : 15
    if (pos === 'b') { anchor = 'middle'; ax = 0; ay = here ? 32 : 24 } else if (pos === 'l') { anchor = 'end'; ax = -off; ay = -((all.length - 1) * lh) / 2 + 5 } else { anchor = 'start'; ax = off; ay = -((all.length - 1) * lh) / 2 + 5 }
    all.forEach((ln, i) => {
      g += `<text x="${ax}" y="${ay + i * lh}" text-anchor="${anchor}" class="${ln.w ? 'it-s' : 'srf'}" font-size="${ln.w ? 14.5 : 15}" font-weight="${here ? 600 : 400}" fill="${ln.w ? GOLD : (here ? '#fff' : (l ? INK2 : LILLT))}">${esc(ln.t)}</text>`
    })
  }
  g += '</g>'
  if (fx.ring === st.key && fx.liftWord) g += `<g class="lift"><text x="${x}" y="${y - 24}" text-anchor="middle" class="it-s" font-size="19" fill="${GOLDHI}" filter="url(#${p}glow)">${esc(fx.liftWord)}</text></g>`
  return g
}

function sealArt () {
  const x = 180; const y = 130
  return `<circle cx="${x}" cy="${y}" r="36" fill="rgba(42,24,69,.92)" stroke="${LIL}" stroke-width="1" stroke-dasharray="2 3"/><circle cx="${x}" cy="${y}" r="27" fill="none" stroke="${LIL}" stroke-width=".7"/>` +
    `<path d="M${x} ${y - 19} L${x + 5} ${y - 5} L${x + 19} ${y} L${x + 5} ${y + 5} L${x} ${y + 19} L${x - 5} ${y + 5} L${x - 19} ${y} L${x - 5} ${y - 5} Z" fill="none" stroke="${LILLT}" stroke-width=".9"/>`
}

/** The Sun's ceremony (and the seal art) as an SVG fragment in the 360-wide frame. */
export function sunCeremony (p = 'qj-') {
  const rays12 = [...Array(12).keys()].map((i) => { const a = i * Math.PI / 6; return `<line x1="${(180 + 40 * Math.cos(a)).toFixed(1)}" y1="${(130 + 40 * Math.sin(a)).toFixed(1)}" x2="${(180 + 66 * Math.cos(a)).toFixed(1)}" y2="${(130 + 66 * Math.sin(a)).toFixed(1)}" stroke="#ffe7b5" stroke-width="1.4" opacity=".85"/>` }).join('')
  const fakeSun = { stars: [] }
  return `<g class="cer"><clipPath id="${p}sunClip"><rect x="0" y="0" width="360" height="172"/></clipPath>` +
    `<g clip-path="url(#${p}sunClip)"><g class="sunup"><circle cx="180" cy="130" r="92" fill="url(#${p}halo)" opacity=".95"/>${rays12}<circle cx="180" cy="130" r="24" fill="url(#${p}sunFill)" data-origin="1"/></g></g>` +
    `<line x1="40" y1="172" x2="320" y2="172" stroke="${GOLD}" stroke-width=".8" opacity=".5"/>` +
    `<g class="rays">${sunRays(fakeSun, 'done')}</g>` +
    `<g class="sunstars">${SLOTS.su.map((sl) => `<path d="${sparkle(sl[0], sl[1], 5.5)}" fill="${LILLT}" opacity=".8"/>`).join('')}</g>` +
    `<clipPath id="${p}hl"><rect x="0" y="0" width="180" height="270"/></clipPath><clipPath id="${p}hr"><rect x="180" y="0" width="180" height="270"/></clipPath>` +
    `<g class="halfL" clip-path="url(#${p}hl)">${sealArt()}</g><g class="halfR" clip-path="url(#${p}hr)">${sealArt()}</g>` +
    `<line class="split" pathLength="100" x1="180" y1="84" x2="180" y2="176" stroke="${GOLDHI}" stroke-width="1.2"/></g>`
}

function drawSeal (j, sec, fx, p) {
  if (fx.ceremony) return sunCeremony(p)
  let s = sealArt()
  if (sec.line) s += `<text x="180" y="200" text-anchor="middle" class="it-s" font-size="15" fill="${LILLT}">${esc(sec.line)}</text>`
  return s
}

/** One level on its own (DESIGN §18), the built block in its own frame. */
export function drawLevel (j, k, fx = {}, pfx = 'qf') {
  const key = sectionKey(k)
  if (!j || !key || !j.sections[key]) return ''
  const p = `${pfx}${key}-`
  return `<svg class="sky lvl" viewBox="0 0 ${SKY_W} ${SEC[key].h}" role="img" aria-label="${esc(j.sections[key].name)}">${defs(p)}${drawBlock(j, key, 0, fx, p)}</svg>`
}

function drawSaturn (sec) {
  const cx = 180; const cy = 98
  let s = `<g class="st" data-key="saturn" role="button" tabindex="0" aria-label="Saturn, coming">` +
    `<rect class="hit" x="90" y="50" width="180" height="96" fill="transparent"/>` +
    `<circle cx="${cx}" cy="${cy}" r="21" fill="#21173c" stroke="${LIL}" stroke-width=".8" stroke-dasharray="1.5 3.5" opacity=".7"/>` +
    `<ellipse cx="${cx}" cy="${cy}" rx="52" ry="12" fill="none" stroke="${LIL}" stroke-width=".8" stroke-dasharray="1.5 4" transform="rotate(-16 ${cx} ${cy})" opacity=".6"/>` +
    `<ellipse cx="${cx}" cy="${cy}" rx="66" ry="16" fill="none" stroke="${LIL}" stroke-width=".7" stroke-dasharray="1 5" transform="rotate(-16 ${cx} ${cy})" opacity=".45"/></g>`
  s += `<text x="${cx}" y="160" text-anchor="middle" class="dsp" font-size="8" letter-spacing="2" fill="${LIL}" opacity=".7">COMING</text>`
  const c = sec.coming
  const a = c.slice(0, 3).join(' · '); const b = c.slice(3).join(' · ')
  if (a) s += `<text x="${cx}" y="180" text-anchor="middle" class="it-s" font-size="14" fill="${DIM}">${esc(a)}</text>`
  if (b) s += `<text x="${cx}" y="197" text-anchor="middle" class="it-s" font-size="14" fill="${DIM}">${esc(b)}</text>`
  return s
}

// ── the small glyphs (strip, list, hero art) ──────────────────────────────
/** A constellation's mini glyph, the inside of a 32×32 SVG. */
export function miniGlyph (k, on) {
  const c = on ? GOLDHI : LILLT; const l = on ? GOLD : LIL
  const sp = (x, y, r) => `<path d="${sparkle(x, y, r)}" fill="${c}"/>`
  const ln = (a, b, cc, dd) => `<line x1="${a}" y1="${b}" x2="${cc}" y2="${dd}" stroke="${l}" stroke-width="1.2"/>`
  switch (k) {
    case 'fs': return sp(16, 16, 9) + ln(16, 4, 16, 28) + ln(4, 16, 28, 16)
    case 'ri': return ln(3, 22, 29, 22) + sp(8, 22, 3.5) + sp(15, 13, 3.5) + sp(24, 8, 3.5)
    case 've': return `<circle cx="16" cy="12" r="7" fill="none" stroke="${l}" stroke-width="1.3"/>` + ln(16, 19, 16, 29) + ln(11, 25, 21, 25) + sp(16, 5, 3.4)
    case 'zw': return `<circle cx="16" cy="16" r="11" fill="none" stroke="${l}" stroke-width="1.2"/>` + [...Array(12).keys()].map((i) => `<circle cx="${(16 + 11 * Math.cos(i * Math.PI / 6)).toFixed(1)}" cy="${(16 + 11 * Math.sin(i * Math.PI / 6)).toFixed(1)}" r="1.3" fill="${c}"/>`).join('') + sp(16, 16, 3)
    case 'mo': return `<path d="M19 4.5 A11.5 11.5 0 1 0 19 27.5 A9 11.5 0 0 1 19 4.5 Z" fill="${c}"/>`
    case 'su': return `<circle cx="16" cy="16" r="6" fill="${c}"/>` + [...Array(8).keys()].map((i) => { const a = i * Math.PI / 4; return ln((16 + 9 * Math.cos(a)).toFixed(1), (16 + 9 * Math.sin(a)).toFixed(1), (16 + 13 * Math.cos(a)).toFixed(1), (16 + 13 * Math.sin(a)).toFixed(1)) }).join('')
    case 'vo': return [[16, 7, 3.4], [9, 14, 2.6], [22, 13, 2.8], [14, 22, 2.4], [23, 23, 2.6], [16, 16, 2]].map(([x, y, r]) => sp(x, y, r)).join('')
    case 'sa': return `<circle cx="16" cy="16" r="6" fill="none" stroke="${LIL}" stroke-width="1" stroke-dasharray="1.5 2"/><ellipse cx="16" cy="16" rx="13" ry="4" fill="none" stroke="${LIL}" stroke-width="1" stroke-dasharray="1.5 2.5" transform="rotate(-16 16 16)"/>`
    default: return ''
  }
}
